import {useContext} from 'react';

import {useTranslation} from 'react-i18next';

import {Grid, Typography} from '@mui/material';

import {LanguageContext} from '../../../../../../contexts/LanguageContext/LanguageContext';
import {greenColors} from '../../../../../../layouts/theme/designSystem';
import {MatchStatus} from '../../../../../../types/MatchStatus/MatchStatus';
import {transformDate} from '../../../../../../utils/dateUtils/dateFormattingUtils';
import {snakeToCamel} from '../../../../../../utils/matchUtils';

type ActivityType = 'match-created' | 'status-change' | 'note-authored';

interface Props {
  date: string;
  authorName: string;
  activityType: ActivityType;
  from?: MatchStatus | null;
  to?: MatchStatus | null;
}
export const MatchActivityMetaData = ({date, authorName, activityType, from, to}: Props) => {
  const lang = useContext(LanguageContext);
  const {t: activity} = useTranslation('translation', {
    keyPrefix: 'companyApplicants.applicantsCard.cardDetails.companyMatchNotes.matchActivity',
  });
  const {t: statusText} = useTranslation('translation', {keyPrefix: 'companyApplicants.applicantsCard.menuItems'});

  const formatAdditionalText = (type: ActivityType): string => {
    switch (type) {
      case 'match-created':
        return activity('matchCreated');
      case 'note-authored':
        return `${activity('commentCreated')}:`;
      case 'status-change':
        return activity('statusChange');
    }
  };

  const handleStatusText = (status: MatchStatus): string => {
    if (status === MatchStatus.Accepted) {
      return statusText('new');
    } else if (status === MatchStatus.OnHold) {
      return statusText(snakeToCamel(status));
    }
    return statusText(status.toLowerCase());
  };

  return (
    <Grid container flexWrap='nowrap' justifyContent='space-between'>
      <Grid item container justifyContent='flex-start'>
        <Typography variant='body1' width={'100%'} sx={{color: greenColors[100]}}>
          {transformDate(date, 'numeric')}
        </Typography>
        <Typography fontWeight={700} variant='body1'>
          {authorName}
        </Typography>
        <Typography paddingLeft='6px' variant='body1'>
          {formatAdditionalText(activityType)}
        </Typography>
        {!!from && (
          <Typography fontWeight={700} variant='body1' sx={{ml: 1}}>
            {handleStatusText(from)}
          </Typography>
        )}
        {!!from && (
          <Typography paddingLeft='6px' variant='body1' sx={{ml: 1}}>
            {lang === 'en' ? 'to' : 'zu'}
          </Typography>
        )}
        {!!to && activityType !== 'match-created' && (
          <Typography fontWeight={700} variant='body1' sx={{ml: 1}}>
            {handleStatusText(to)}
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};
